import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LockProvider, useLock } from './context/LockContext';
import { ThemeProvider } from './context/ThemeContext';
import { WorkModeProvider, useWorkMode } from './context/WorkModeContext';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { TourProvider } from './context/TourContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { storage, migrateLocalStorageKeys } from './utils/storage';
import { capturePromoAttribution } from './utils/promoAttribution';
import { ChatProvider } from './context/ChatContext';
import Breadcrumbs from './components/Breadcrumbs';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import TourOverlay from './components/TourOverlay';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRestaurant from './components/RequireRestaurant';
import LockScreen from './components/LockScreen';
import OnboardingWizard from './components/OnboardingWizard';
import { Header, ActivityBar, DockPanel } from './components/layout';
import { NotificationPrompt } from './components/NotificationPrompt';
import ChatWidget from './components/ChatWidget';
import FeatureGateHost from './components/FeatureGateHost';
import { usePushNotifications } from './hooks/usePushNotifications';
import Dashboard from './pages/Dashboard';
import RestaurantPage from './pages/RestaurantPage';
import RestaurantFormPage from './pages/RestaurantFormPage';
import MenuPage from './pages/MenuPage';
import ItemFormPage from './pages/ItemFormPage';
import ComboWizardPage from './pages/ComboWizardPage';
import TablesPage from './pages/TablesPage';
import OrdersPage from './pages/OrdersPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import CashRegisterPage from './pages/CashRegisterPage';
import InventoryPage from './pages/InventoryPage';
import InventoryMovementsPage from './pages/InventoryMovementsPage';
import InventorySetupWizard from './pages/InventorySetupWizard';
import InventoryCountPage from './pages/InventoryCountPage';
import InventoryConsolidatedPage from './pages/InventoryConsolidatedPage';
import InventoryTransfersPage from './pages/InventoryTransfersPage';
import OrgRecipesPage from './pages/OrgRecipesPage';
import CatalogSuggestionsPage from './pages/CatalogSuggestionsPage';
import InventoryReportsPage from './pages/InventoryReportsPage';
import ExpensesPage from './pages/ExpensesPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailsPage from './pages/SupplierDetailsPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import CreatePurchaseOrderPage from './pages/CreatePurchaseOrderPage';
import PurchaseOrderDetailsPage from './pages/PurchaseOrderDetailsPage';
import ModifiersPage from './pages/ModifiersPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import SubscriptionExpiredPage from './pages/SubscriptionExpiredPage';
import SubscriptionExpiredGuard from './components/SubscriptionExpiredGuard';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import SettingsPage from './pages/SettingsPage';
import DomainsPage from './pages/DomainsPage';
import DeliveryIntegrationsPage from './pages/DeliveryIntegrationsPage';
import FiscalConfigPage from './pages/FiscalConfigPage';
import PartnersPage from './pages/PartnersPage';
import PartnerApplyPage from './pages/PartnerApplyPage';
import PartnerPortalPage from './pages/PartnerPortalPage';
import InvoicingPage from './pages/InvoicingPage';
import NotFoundPage from './pages/NotFoundPage';
import PromotionsPage from './pages/PromotionsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CreatingOrgPage from './pages/CreatingOrgPage';
import NewPasswordPage from './pages/NewPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import TermsPage from './pages/TermsPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import { useTranslation } from 'react-i18next';
import './App.css';

// Redirect helpers for backward-compatible route migration
function RedirectSupplierDetail() {
  const { supplierId } = useParams();
  return <Navigate to={`/inventory/suppliers/${supplierId}`} replace />;
}
function RedirectPurchaseOrderDetail() {
  const { orderId } = useParams();
  return <Navigate to={`/inventory/purchase-orders/${orderId}`} replace />;
}

// Inner component that uses sidebar context
function AppContent({ pushNotifications }) {
  const { isSecondarySidebarVisible } = useSidebar();
  const { workMode } = useWorkMode();
  const { t } = useTranslation();
  const [isDockExpanded, setIsDockExpanded] = useState(true);

  const noDock = workMode === 'admin';

  const layoutClass = [
    'app-layout-new',
    isSecondarySidebarVisible ? 'sidebar-pinned' : '',
    noDock ? 'no-dock' : !isDockExpanded ? 'dock-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClass}>
      <a href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </a>
      <Header />
      <ActivityBar />
      <DockPanel isExpanded={isDockExpanded} onToggle={setIsDockExpanded} />
      <TourOverlay />
      {/* Prompt de notificaciones push */}
      <NotificationPrompt
        isSupported={pushNotifications.isSupported}
        isSubscribed={pushNotifications.isSubscribed}
        permission={pushNotifications.permission}
        isLoading={pushNotifications.isLoading}
        error={pushNotifications.error}
        onSubscribe={pushNotifications.subscribe}
      />
      <ChatWidget />
      <FeatureGateHost />
      <main id="main-content" className="main-content-new" tabIndex="-1">
        <Breadcrumbs />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/restaurant" element={<RestaurantPage />} />
          <Route path="/restaurant/new" element={<RestaurantFormPage />} />
          <Route path="/restaurant/edit/:restaurantId" element={<RestaurantFormPage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/menu/items/new" element={<ItemFormPage />} />
          <Route path="/menu/items/edit/:itemId" element={<ItemFormPage />} />
          <Route path="/menu/combos/new" element={<ComboWizardPage />} />
          <Route path="/menu/combos/:itemId/edit" element={<ComboWizardPage />} />
          <Route path="/tables" element={<TablesPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route
            path="/delivery/integrations"
            element={
              <ProtectedRoute allowedRoles={['OWNER', 'ADMIN']}>
                <DeliveryIntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/delivery" element={<Navigate to="/delivery/integrations" replace />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:customerId" element={<CustomerDetailsPage />} />
          <Route path="/cash-register" element={<CashRegisterPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventory/movements" element={<InventoryMovementsPage />} />
          {/* Épica C: wizard de inventario (RES-35…38) y conteo guiado (RES-40) */}
          <Route path="/inventory/setup" element={<InventorySetupWizard />} />
          <Route path="/inventory/count" element={<InventoryCountPage />} />
          {/* Épica D (cadenas): consolidado, transferencias, recetas estándar, sugerencias y reportes (RES-43…47) */}
          <Route path="/inventory/consolidated" element={<InventoryConsolidatedPage />} />
          <Route path="/inventory/transfers" element={<InventoryTransfersPage />} />
          <Route path="/inventory/org-recipes" element={<OrgRecipesPage />} />
          <Route path="/inventory/suggestions" element={<CatalogSuggestionsPage />} />
          <Route path="/inventory/reports" element={<InventoryReportsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/inventory/suppliers" element={<SuppliersPage />} />
          <Route path="/inventory/suppliers/:supplierId" element={<SupplierDetailsPage />} />
          <Route path="/inventory/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/inventory/purchase-orders/new" element={<CreatePurchaseOrderPage />} />
          <Route
            path="/inventory/purchase-orders/:orderId"
            element={<PurchaseOrderDetailsPage />}
          />
          <Route path="/menu/modifiers" element={<ModifiersPage />} />
          {/* Backward-compatible redirects for old routes */}
          <Route path="/suppliers" element={<Navigate to="/inventory/suppliers" replace />} />
          <Route path="/suppliers/:supplierId" element={<RedirectSupplierDetail />} />
          <Route
            path="/purchase-orders"
            element={<Navigate to="/inventory/purchase-orders" replace />}
          />
          <Route
            path="/purchase-orders/new"
            element={<Navigate to="/inventory/purchase-orders/new" replace />}
          />
          <Route path="/purchase-orders/:orderId" element={<RedirectPurchaseOrderDetail />} />
          <Route path="/modifiers" element={<Navigate to="/menu/modifiers" replace />} />
          <Route path="/finances" element={<Navigate to="/cash-register" replace />} />
          <Route
            path="/invoicing"
            element={
              <ProtectedRoute allowedRoles={['OWNER', 'ADMIN']}>
                <InvoicingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fiscal-config"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <FiscalConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plan-selection"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <PlanSelectionPage />
              </ProtectedRoute>
            }
          />
          <Route path="/menu/promo-rules" element={<PromotionsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/domains"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <DomainsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/edit/:userId"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route path="/restaurants" element={<RestaurantPage />} />
          <Route
            path="/partners"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <PartnersPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
}

function AppLayout() {
  const [restaurantId, setRestaurantId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const { isLocked } = useLock();
  const { user, organization } = useAuth();
  const {
    hasActiveSubscription,
    initialized,
    loading: subscriptionLoading,
    error: subscriptionError,
    refreshSubscription,
    isExpired,
  } = useSubscription();

  // Obtener restaurantId de localStorage
  useEffect(() => {
    const getRestaurantId = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    getRestaurantId();
    window.addEventListener('storage', getRestaurantId);
    const handleRestaurantChange = () => getRestaurantId();
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    return () => {
      window.removeEventListener('storage', getRestaurantId);
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
    };
  }, []);

  // Verificar si debe mostrar el onboarding wizard
  // Reactivo: también oculta el wizard si la suscripción se carga tarde (post-login race condition)
  useEffect(() => {
    // Esperar a que la verificación termine (no decidir mientras está cargando)
    if (!initialized || subscriptionLoading || !user || !organization) return;

    // Una suscripción vencida (paywall) cuenta como "tiene suscripción": no se
    // ofrece el wizard de alta; el guard la manda a /subscription/expired.
    const hasSub = hasActiveSubscription() || isExpired;
    console.log('[AppLayout] Verificación de suscripción:', {
      hasActiveSubscription: hasSub,
      subscriptionError,
      showOnboarding,
      user: user?.username,
      organization: organization?.organizationId,
    });

    if (hasSub && showOnboarding) {
      // Suscripción cargó tarde — ocultar wizard
      console.log('[AppLayout] Suscripción detectada, ocultando wizard');
      setShowOnboarding(false);
    } else if (!hasSub && !subscriptionError && !showOnboarding && !initialCheckDone) {
      // No tiene suscripción — mostrar wizard (solo la primera vez)
      console.log('[AppLayout] No tiene suscripción activa, mostrando wizard');
      setShowOnboarding(true);
      setInitialCheckDone(true);
    }
  }, [
    initialized,
    subscriptionLoading,
    user,
    organization,
    hasActiveSubscription,
    isExpired,
    subscriptionError,
    showOnboarding,
    initialCheckDone,
  ]);

  // Hook de Push Notifications
  const pushNotifications = usePushNotifications(restaurantId, 'admin', user?.userId);

  // Manejador para cuando se completa el onboarding
  const handleOnboardingComplete = async () => {
    console.log('[AppLayout] Onboarding completado, refrescando suscripción...');
    // Refrescar el estado de suscripción para asegurar que se actualice
    await refreshSubscription();
    setShowOnboarding(false);
  };

  return (
    <SubscriptionExpiredGuard>
      {isLocked ? (
        <LockScreen />
      ) : (
        <>
          {/* Mostrar wizard de onboarding si no tiene suscripción */}
          <OnboardingWizard isOpen={showOnboarding} onComplete={handleOnboardingComplete} />

          <RequireRestaurant>
            <ChatProvider>
              <WorkModeProvider>
                <SidebarProvider>
                  <TourProvider>
                    <BreadcrumbProvider>
                      <AppContent pushNotifications={pushNotifications} />
                    </BreadcrumbProvider>
                  </TourProvider>
                </SidebarProvider>
              </WorkModeProvider>
            </ChatProvider>
          </RequireRestaurant>
        </>
      )}
    </SubscriptionExpiredGuard>
  );
}

// Run one-time localStorage key migration (un-prefixed → admin_ prefixed)
migrateLocalStorageKeys();

// Capturar atribución de partner (NER-3079) del link de signup / cookie una vez
// al cargar la app, para que sobreviva el wizard de registro multi-paso.
capturePromoAttribution();

// Detect basename dynamically: use /resto/admin when the URL path starts with
// that prefix (unified hosting at nerdo.solutions/resto/admin), otherwise empty
// (standalone hosting at admin.nerdo.solutions or CloudFront root).
const basename = window.location.pathname.startsWith('/resto/admin') ? '/resto/admin' : '';

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <AuthProvider>
          <SubscriptionProvider>
            <LockProvider>
              <Router basename={basename}>
                <CookieConsent />
                <Routes>
                  {/* Rutas públicas */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  {/* NER-3781: callback del flujo Google admin (custom OAuth) */}
                  <Route path="/creating-org" element={<CreatingOrgPage />} />
                  <Route path="/new-password" element={<NewPasswordPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  {/* RES-94: solicitud pública para ser partner (sin login) */}
                  <Route path="/partners/apply" element={<PartnerApplyPage />} />
                  {/* RES-93: portal del partner (rol PARTNER), fuera del layout del restaurante */}
                  <Route
                    path="/partner"
                    element={
                      <ProtectedRoute allowedRoles={['PARTNER']}>
                        <PartnerPortalPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Paywall de fin de trial: pantalla completa, fuera del layout */}
                  <Route
                    path="/subscription/expired"
                    element={
                      <ProtectedRoute>
                        {/* TourProvider: lo necesita HelpIcon (sin TourOverlay no se ve tour) */}
                        <TourProvider>
                          <SubscriptionExpiredPage />
                        </TourProvider>
                      </ProtectedRoute>
                    }
                  />

                  {/* Rutas protegidas */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Router>
            </LockProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
